import React, { useState, useRef, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { useMediaUpload, getMediaUrl } from '../../../hooks/useMediaUpload';

interface ProfilePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
  }) => Promise<void>;
  currentUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
  } | null;
  profileUser: {
    id: string;
    username: string;
    displayName?: string;
  };
  theme: {
    textColor: string;
    accentColor: string;
    cardBg: string;
    highlightColor?: string;
  };
  scale: { w: number; h: number };
  scaleW: (val: number, scale: number) => number;
  scaleH: (val: number, scale: number) => number;
  getCardBackground: (theme: any, customization: any, fallback: string) => string;
  customization?: any;
  cardPosition?: 'left' | 'center' | 'right';
}

export function ProfilePostModal({
  isOpen,
  onClose,
  onSubmit,
  currentUser,
  profileUser,
  theme,
  scale,
  scaleW,
  scaleH,
  getCardBackground,
  customization,
  cardPosition = 'center'
}: ProfilePostModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaPosition, setMediaPosition] = useState<'above' | 'below'>('below');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading, uploadProgress, fileId, error: uploadError } = useMediaUpload();

  // Update media URL when file upload completes
  useEffect(() => {
    if (fileId) {
      setMediaUrl(getMediaUrl(fileId));
    }
  }, [fileId]);

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      const tags = tagInput
        .split(/[,\s#]+/)
        .map(t => t.trim())
        .filter(t => t.length > 0);

      await onSubmit({
        title,
        content,
        tags,
        mediaUrl,
        mediaPosition
      });

      // Reset form
      setTitle('');
      setContent('');
      setTagInput('');
      setMediaUrl('');
      setMediaPosition('below');
      onClose();
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadFile(file);
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Failed to upload file. Please try again.');
      }
    }
    if (e.target) e.target.value = '';
  };

  // Detect mobile mode via scale (mobile sets scale to {w:1, h:1})
  const isMobileModal = scale.w === 1 && scale.h === 1;

  if (!isOpen) return null;

  return (
    <>
    {/* Mobile backdrop */}
    {isMobileModal && (
      <div
        className="fixed inset-0"
        style={{ zIndex: 99, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        onTouchStart={onClose}
      />
    )}
    <div
      className="fixed rounded-xl shadow-2xl border"
      style={isMobileModal ? {
        // Mobile: bottom-sheet style
        bottom: 0,
        left: 0,
        right: 0,
        top: 'auto',
        backgroundColor: theme.cardBg,
        borderColor: theme.accentColor + '60',
        zIndex: 100,
        width: '100%',
        maxHeight: '85vh',
        overflow: 'auto',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        animation: 'slideUp 0.3s ease-out',
      } : {
        // Desktop: original positioning
        top: cardPosition === 'left' ? '13.3rem' : '10rem',
        ...(cardPosition === 'left'
          ? { right: '1.5rem' }
          : { left: '1.5rem' }),
        backgroundColor: theme.cardBg,
        borderColor: theme.accentColor + '60',
        zIndex: 43,
        width: '22vw',
        maxWidth: '600px',
        minWidth: '350px',
        animation: 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
        <div style={{ padding: `${scaleH(20, scale.h)}px` }}>
          {/* Header */}
          <div className="flex items-center justify-between" style={{ marginBottom: `${scaleH(16, scale.h)}px` }}>
            <h3 className="font-bold" style={{ color: theme.textColor, fontSize: `${scaleW(20, scale.w)}px`, marginLeft: `${scaleW(8, scale.w)}px` }}>
              Post to {profileUser.displayName || profileUser.username} Profile
            </h3>
            <button
              onClick={onClose}
              className="font-bold hover:opacity-70 transition-opacity"
              style={{ color: theme.textColor, marginRight: `${scaleW(8, scale.w)}px`, fontSize: `${scaleW(32, scale.w)}px` }}
            >
              ×
            </button>
          </div>

          {/* Title Input */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post Title (optional)"
            className="w-full rounded-lg focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: theme.textColor,
              border: `1px solid ${theme.accentColor}40`,
              padding: `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px`,
              fontSize: `${scaleW(16, scale.w)}px`,
              marginBottom: `${scaleH(12, scale.h)}px`,
              boxShadow: '0 0 0 0px transparent'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = `0 0 0 2px ${theme.highlightColor || theme.accentColor}`;
              e.target.style.borderColor = theme.highlightColor || theme.accentColor;
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = '0 0 0 0px transparent';
              e.target.style.borderColor = theme.accentColor + '40';
            }}
          />

          {/* Content Textarea */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            className="w-full rounded-lg resize-none focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: theme.textColor,
              border: `1px solid ${theme.accentColor}40`,
              padding: `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px`,
              fontSize: `${scaleW(16, scale.w)}px`,
              minHeight: `${scaleH(120, scale.h)}px`,
              marginBottom: `${scaleH(12, scale.h)}px`,
              boxShadow: '0 0 0 0px transparent'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = `0 0 0 2px ${theme.highlightColor || theme.accentColor}`;
              e.target.style.borderColor = theme.highlightColor || theme.accentColor;
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = '0 0 0 0px transparent';
              e.target.style.borderColor = theme.accentColor + '40';
            }}
          />

          {/* Media Section */}
          <div style={{ marginBottom: `${scaleH(12, scale.h)}px` }}>
            <input
              type="text"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="Image URL (optional)"
              className="w-full rounded-lg focus:outline-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: theme.textColor,
                border: `1px solid ${theme.accentColor}40`,
                padding: `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px`,
                fontSize: `${scaleW(16, scale.w)}px`,
                marginBottom: `${scaleH(8, scale.h)}px`,
                boxShadow: '0 0 0 0px transparent'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 2px ${theme.highlightColor || theme.accentColor}`;
                e.target.style.borderColor = theme.highlightColor || theme.accentColor;
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = '0 0 0 0px transparent';
                e.target.style.borderColor = theme.accentColor + '40';
              }}
            />
            <div className="flex" style={{ gap: `${scaleW(8, scale.w)}px` }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center rounded-lg transition-all disabled:opacity-50"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`,
                  paddingLeft: `${scaleW(16, scale.w)}px`,
                  paddingRight: `${scaleW(16, scale.w)}px`,
                  paddingTop: `${scaleH(8, scale.h)}px`,
                  paddingBottom: `${scaleH(8, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`,
                  gap: `${scaleW(8, scale.w)}px`
                }}
              >
                <Upload size={Math.round(scaleW(16, scale.w))} />
                {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Media'}
              </button>
              {mediaUrl && (
                <button
                  onClick={() => setMediaUrl('')}
                  className="flex items-center rounded-lg transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 0, 0, 0.2)',
                    color: theme.textColor,
                    border: '1px solid rgba(255, 0, 0, 0.4)',
                    paddingLeft: `${scaleW(16, scale.w)}px`,
                    paddingRight: `${scaleW(16, scale.w)}px`,
                    paddingTop: `${scaleH(8, scale.h)}px`,
                    paddingBottom: `${scaleH(8, scale.h)}px`,
                    fontSize: `${scaleW(14, scale.w)}px`,
                    gap: `${scaleW(8, scale.w)}px`
                  }}
                >
                  <X size={Math.round(scaleW(16, scale.w))} />
                  Clear
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Media Position Selector */}
            <div className="flex" style={{ gap: `${scaleW(8, scale.w)}px`, marginTop: `${scaleH(12, scale.h)}px` }}>
              <button
                onClick={() => setMediaPosition('above')}
                className="flex-1 rounded-lg transition-all font-medium"
                style={{
                  backgroundColor: mediaPosition === 'above' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: mediaPosition === 'above' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${mediaPosition === 'above' ? theme.accentColor : theme.accentColor + '30'}`,
                  paddingLeft: `${scaleW(12, scale.w)}px`,
                  paddingRight: `${scaleW(12, scale.w)}px`,
                  paddingTop: `${scaleH(8, scale.h)}px`,
                  paddingBottom: `${scaleH(8, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                Above Content
              </button>
              <button
                onClick={() => setMediaPosition('below')}
                className="flex-1 rounded-lg transition-all font-medium"
                style={{
                  backgroundColor: mediaPosition === 'below' ? theme.accentColor : 'rgba(255, 255, 255, 0.05)',
                  color: mediaPosition === 'below' ? '#ffffff' : theme.textColor,
                  border: `1px solid ${mediaPosition === 'below' ? theme.accentColor : theme.accentColor + '30'}`,
                  paddingLeft: `${scaleW(12, scale.w)}px`,
                  paddingRight: `${scaleW(12, scale.w)}px`,
                  paddingTop: `${scaleH(8, scale.h)}px`,
                  paddingBottom: `${scaleH(8, scale.h)}px`,
                  fontSize: `${scaleW(14, scale.w)}px`
                }}
              >
                Below Content
              </button>
            </div>
          </div>

          {/* Tags Input */}
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Tags (e.g. #tag1 #tag2 or tag1, tag2)"
            className="w-full rounded-lg focus:outline-none transition-all"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: theme.textColor,
              border: `1px solid ${theme.accentColor}40`,
              padding: `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px`,
              fontSize: `${scaleW(16, scale.w)}px`,
              marginBottom: `${scaleH(16, scale.h)}px`,
              boxShadow: '0 0 0 0px transparent'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = `0 0 0 2px ${theme.highlightColor || theme.accentColor}`;
              e.target.style.borderColor = theme.highlightColor || theme.accentColor;
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = '0 0 0 0px transparent';
              e.target.style.borderColor = theme.accentColor + '40';
            }}
          />

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="w-full rounded-lg font-semibold transition-all disabled:opacity-50"
            style={{
              backgroundColor: theme.accentColor,
              color: theme.textColor,
              padding: `${scaleH(12, scale.h)}px ${scaleW(16, scale.w)}px`,
              fontSize: `${scaleW(16, scale.w)}px`
            }}
          >
            {isSubmitting ? 'Posting...' : 'Post to Profile'}
          </button>
        </div>
    </div>
    </>
  );
}
